import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from 'models/order';
import { Box, Grid, Typography } from '@mui/material';
import BoxWithHighLighting from 'components/with-hith-light-components/box-with-highlighting.component';
import { Organization } from 'models/organization';
import { Address } from 'models/contact';

type DeliveryClinicPostalAddressProps = {
    order: Order;
};
const DeliveryClinicPostalAddress: FC<DeliveryClinicPostalAddressProps> = ({
    order,
}: DeliveryClinicPostalAddressProps) => {
    const { t: translate } = useTranslation(['common', 'organization', 'transport', 'country-names']);
    const [postalAddress, setPostalAddress] = useState<Address>({} as Address);
    const [originalOrderPostalAddress, setOriginalOrderPostalAddress] = useState<Address>({} as Address);
    const [destination, setDestination] = useState<Organization>({} as Organization);
    const [originalOrderDestination, setOriginalOrderDestination] = useState<Organization>({} as Organization);
    const [destinationCountryName, setDestinationCountryName] = useState('');
    const [originalOrderDestinationCountryName, setOriginalOrderDestinationCountryName] = useState('');

    useEffect(() => {
        if (order.deliveryDetails.destination.contact.postalAddress) {
            setPostalAddress(order.deliveryDetails.destination.contact.postalAddress);
            setOriginalOrderPostalAddress(
                order.originalOrder.deliveryDetails.destination.contact.postalAddress || ({} as Address),
            );
        }
        setDestination(order.deliveryDetails.destination);
        setOriginalOrderDestination(order.originalOrder.deliveryDetails.destination);
        setDestinationCountryName(
            translate(`country-names:COUNTRY_${order.deliveryDetails.destination.contact?.address?.country || ''}`),
        );
        setOriginalOrderDestinationCountryName(
            translate(
                `country-names:COUNTRY_${order.originalOrder.deliveryDetails.destination.contact?.address?.country || ''}`,
            ),
        );
    }, [order]);

    return (
        <Box mb={2}>
            <Box mb={1} display="flex" alignItems="flex-end">
                <Typography variant="h4">{translate('transport:PLACE_OF_DELIVERY')}</Typography>
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2">{translate('common:NAME')}</Typography>
                    <BoxWithHighLighting originalData={originalOrderDestination.name} data={destination.name} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2">{translate('organization:ADDRESS.ADDRESS')}</Typography>
                    <BoxWithHighLighting
                        originalData={
                            originalOrderPostalAddress?.street ||
                            (originalOrderDestination?.contact?.address?.street as string)
                        }
                        data={postalAddress?.street || (destination?.contact?.address?.street as string)}
                    />
                    <Box display="flex">
                        <BoxWithHighLighting
                            sx={{ marginRight: 0.5 }}
                            originalData={
                                originalOrderPostalAddress?.postalCode ||
                                (originalOrderDestination?.contact?.address?.postalCode as string)
                            }
                            data={postalAddress?.postalCode || (destination?.contact?.address?.postalCode as string)}
                        />
                        <BoxWithHighLighting
                            sx={{ marginRight: 0.5 }}
                            originalData={
                                originalOrderPostalAddress?.city ||
                                (originalOrderDestination?.contact?.address?.city as string)
                            }
                            data={postalAddress?.city || (destination?.contact?.address?.city as string)}
                        />
                        <BoxWithHighLighting
                            originalData={originalOrderDestinationCountryName}
                            data={destinationCountryName}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DeliveryClinicPostalAddress;
